/**
 * RuleListView — Reusable rule list component
 * Displays rules filtered by context with name, condition summary, action summary, active status, edit/delete.
 * Spec: Section 8.3
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { rulesEngineService } from '../../services/api/rulesEngineService';
import ConfirmDialog from '../common/ConfirmDialog';
import type { Rule } from '../../types/rulesEngine';

interface Props {
  context: string;
  onEdit: (rule: Rule) => void;
  onCreate: () => void;
  onCopy?: (rule: Rule) => void;
  refreshKey?: number;
  /** Override the list header title. Falls back to generic "Rules" if not provided. */
  title?: string;
  /** Custom condition summary renderer. Falls back to generic count if not provided. */
  conditionSummaryFn?: (rule: Rule) => string;
  /** Custom action summary renderer. Column hidden if not provided. */
  actionSummaryFn?: (rule: Rule) => string;
  /** Override the action summary column header. Falls back to rules.actionSummary i18n key. */
  actionSummaryLabel?: string;
  /** Optional timing summary renderer. Shows a "Timing" column when provided. */
  timingSummaryFn?: (rule: Rule) => string;
  /** Pre-filter rules before column filters apply (e.g. by product type). */
  ruleFilterFn?: (rule: Rule) => boolean;
  /** Called after a rule is deleted so sibling panels can reload. */
  onRulesChanged?: () => void;
  /** Allow deletion and copying of catch-all (no-condition) rules. Default false. */
  allowDeleteCatchAll?: boolean;
  /** Extra summary columns rendered between Conditions and Action Summary. */
  extraColumns?: { key: string; label: string; fn: (rule: Rule) => string }[];
}

const RuleListView: React.FC<Props> = ({ context, onEdit, onCreate, onCopy, refreshKey, title: titleProp, conditionSummaryFn, actionSummaryFn, actionSummaryLabel, timingSummaryFn, ruleFilterFn, onRulesChanged, allowDeleteCatchAll, extraColumns }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<Rule | null>(null);

  // Column filters
  const [filterName, setFilterName] = useState('');
  const [filterConditions, setFilterConditions] = useState('');
  const [filterActionSummary, setFilterActionSummary] = useState('');
  const [filterTiming, setFilterTiming] = useState('');
  const [filterActive, setFilterActive] = useState('');

  // Extra column filters (keyed by extraColumns[].key)
  const [extraFilters, setExtraFilters] = useState<Record<string, string>>({});

  // Sorting
  type SortColumn = 'name' | 'conditions' | 'actionSummary' | 'timing' | 'active' | string;
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const sortIndicator = (col: SortColumn) =>
    sortColumn === col ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';

  const hasActiveFilters = !!(filterName || filterConditions || filterActionSummary || filterTiming || filterActive || Object.values(extraFilters).some(v => v));

  const clearFilters = () => {
    setFilterName('');
    setFilterConditions('');
    setFilterActionSummary('');
    setFilterTiming('');
    setFilterActive('');
    setExtraFilters({});
  };

  // Pre-filter rules (e.g. by product type) before column filters apply
  const baseRules = useMemo(() =>
    ruleFilterFn ? rules.filter(ruleFilterFn) : rules,
  [rules, ruleFilterFn]);

  const isCatchAll = (rule: Rule): boolean =>
    !rule.conditionGroups || rule.conditionGroups.length === 0;

  const defaultConditionSummary = (rule: Rule): string => {
    if (isCatchAll(rule)) {
      return t('rules.defaultRule', 'Applies to all trades');
    }
    const totalConditions = rule.conditionGroups.reduce(
      (sum, g) => sum + g.conditions.length, 0
    );
    return t('rules.conditionCount', '{{count}} condition(s)', { count: totalConditions });
  };

  const getConditionSummary = conditionSummaryFn || defaultConditionSummary;

  const filteredRules = useMemo(() => {
    return baseRules.filter(r => {
      if (filterName && !r.name.toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterConditions && !getConditionSummary(r).toLowerCase().includes(filterConditions.toLowerCase())) return false;
      if (filterActionSummary && actionSummaryFn && !actionSummaryFn(r).toLowerCase().includes(filterActionSummary.toLowerCase())) return false;
      if (filterTiming && timingSummaryFn && !timingSummaryFn(r).toLowerCase().includes(filterTiming.toLowerCase())) return false;
      if (filterActive === 'active' && !r.isActive) return false;
      if (filterActive === 'inactive' && r.isActive) return false;
      if (extraColumns) {
        for (const col of extraColumns) {
          const fv = extraFilters[col.key];
          if (fv && !col.fn(r).toLowerCase().includes(fv.toLowerCase())) return false;
        }
      }
      return true;
    });
  }, [baseRules, filterName, filterConditions, filterActionSummary, filterTiming, filterActive, getConditionSummary, actionSummaryFn, timingSummaryFn, extraColumns, extraFilters]);

  const sortedRules = useMemo(() => {
    if (!sortColumn) return filteredRules;
    const sorted = [...filteredRules].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      switch (sortColumn) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'conditions':
          aVal = getConditionSummary(a).toLowerCase();
          bVal = getConditionSummary(b).toLowerCase();
          break;
        case 'actionSummary':
          aVal = actionSummaryFn ? actionSummaryFn(a).toLowerCase() : '';
          bVal = actionSummaryFn ? actionSummaryFn(b).toLowerCase() : '';
          break;
        case 'timing':
          aVal = timingSummaryFn ? timingSummaryFn(a).toLowerCase() : '';
          bVal = timingSummaryFn ? timingSummaryFn(b).toLowerCase() : '';
          break;
        case 'active':
          aVal = a.isActive ? '1' : '0';
          bVal = b.isActive ? '1' : '0';
          break;
        default: {
          // Extra columns — match by key
          const extraCol = extraColumns?.find(c => c.key === sortColumn);
          if (extraCol) {
            aVal = extraCol.fn(a).toLowerCase();
            bVal = extraCol.fn(b).toLowerCase();
          }
          break;
        }
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredRules, sortColumn, sortDirection, getConditionSummary, actionSummaryFn, timingSummaryFn, extraColumns]);

  const loadRules = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await rulesEngineService.listRules(clientId, context);
      if (response.success && response.data) {
        setRules(response.data.rules);
      }
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, context]);

  useEffect(() => {
    loadRules();
  }, [loadRules, refreshKey]);

  const handleDelete = async (rule: Rule) => {
    try {
      await rulesEngineService.deleteRule(clientId, rule.id);
      setDeleteConfirm(null);
      loadRules();
      onRulesChanged?.();
    } catch (err) {
      console.error('Failed to delete rule:', err);
    }
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="spinner" />
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className="rule-list-view">
      <div className="rule-list-header">
        <h3>
          {titleProp || t('rules.title', 'Rules')}
          {hasActiveFilters && baseRules.length > 0 && (
            <span className="filtered-count">
              {t('rules.showingFiltered', 'Showing {{count}} of {{total}}', { count: filteredRules.length, total: baseRules.length })}
            </span>
          )}
        </h3>
        <button className="add-rule-btn" onClick={onCreate}>
          + {t('rules.addRule', 'Add Rule')}
        </button>
      </div>

      {baseRules.length === 0 ? (
        <div className="no-rules">{t('rules.noRules', 'No rules configured yet.')}</div>
      ) : (
        <div className="rules-table-container">
          <table className="rules-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('name')}>{t('rules.name', 'Name')}{sortIndicator('name')}</th>
                <th className="sortable-header" onClick={() => handleSort('conditions')}>{t('rules.conditions', 'Conditions')}{sortIndicator('conditions')}</th>
                {extraColumns?.map(col => (
                  <th key={col.key} className="sortable-header" onClick={() => handleSort(col.key)}>{col.label}{sortIndicator(col.key)}</th>
                ))}
                {actionSummaryFn && <th className="sortable-header" onClick={() => handleSort('actionSummary')}>{actionSummaryLabel || t('rules.actionSummary', 'Signature Config.')}{sortIndicator('actionSummary')}</th>}
                {timingSummaryFn && <th className="sortable-header" onClick={() => handleSort('timing')}>{t('rules.timingColumn', 'Timing')}{sortIndicator('timing')}</th>}
                <th className="col-active sortable-header" onClick={() => handleSort('active')}>{t('rules.isActive', 'Active')}{sortIndicator('active')}</th>
                <th className="col-actions">
                  {t('common.actions', 'Actions')}
                  {hasActiveFilters && (
                    <button className="clear-filters-btn" onClick={clearFilters} title={t('rules.clearFilters', 'Clear filters')}>
                      ✕
                    </button>
                  )}
                </th>
              </tr>
              <tr className="filter-row">
                <th>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder={t('rules.filterName', 'Filter...')}
                    value={filterName}
                    onChange={e => setFilterName(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder={t('rules.filterName', 'Filter...')}
                    value={filterConditions}
                    onChange={e => setFilterConditions(e.target.value)}
                  />
                </th>
                {extraColumns?.map(col => (
                  <th key={col.key}>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder={t('rules.filterName', 'Filter...')}
                      value={extraFilters[col.key] || ''}
                      onChange={e => setExtraFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                    />
                  </th>
                ))}
                {actionSummaryFn && (
                  <th>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder={t('rules.filterName', 'Filter...')}
                      value={filterActionSummary}
                      onChange={e => setFilterActionSummary(e.target.value)}
                    />
                  </th>
                )}
                {timingSummaryFn && (
                  <th>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder={t('rules.filterName', 'Filter...')}
                      value={filterTiming}
                      onChange={e => setFilterTiming(e.target.value)}
                    />
                  </th>
                )}
                <th>
                  <select className="filter-select" value={filterActive} onChange={e => setFilterActive(e.target.value)}>
                    <option value="">{t('rules.filterAll', 'All')}</option>
                    <option value="active">{t('rules.filterActive', 'Active')}</option>
                    <option value="inactive">{t('rules.filterInactive', 'Inactive')}</option>
                  </select>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRules.map(rule => {
                const catchAll = isCatchAll(rule);
                return (
                  <tr key={rule.id} className={!rule.isActive && !catchAll ? 'inactive-rule' : ''}>
                    <td className="col-name">
                      <div className="rule-name">{rule.name}</div>
                    </td>
                    <td className="col-conditions">
                      <span className={catchAll ? 'default-badge' : 'condition-summary'}>
                        {getConditionSummary(rule)}
                      </span>
                    </td>
                    {extraColumns?.map(col => (
                      <td key={col.key} className="col-extra-summary">
                        <span className="extra-summary">{col.fn(rule)}</span>
                      </td>
                    ))}
                    {actionSummaryFn && (
                      <td className="col-action-summary">
                        <span className="action-summary">{actionSummaryFn(rule)}</span>
                      </td>
                    )}
                    {timingSummaryFn && (
                      <td className="col-timing">
                        <span className="timing-summary">{timingSummaryFn(rule)}</span>
                      </td>
                    )}
                    <td className="col-active">
                      <span className={`active-badge ${rule.isActive ? 'active' : 'inactive'}`}>
                        {rule.isActive ? '✓' : '–'}
                      </span>
                    </td>
                    <td className="col-actions">
                      <div className="rule-actions">
                        <button className="action-btn" onClick={() => onEdit(rule)}>
                          {t('common.edit', 'Edit')}
                        </button>
                        {onCopy && (!catchAll || allowDeleteCatchAll) && (
                          <button className="action-btn" onClick={() => onCopy(rule)}>
                            {t('rules.copy', 'Copy')}
                          </button>
                        )}
                        {(!catchAll || allowDeleteCatchAll) && (
                          <button className="action-btn danger" onClick={() => setDeleteConfirm(rule)}>
                            {t('common.delete', 'Delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sortedRules.length === 0 && (
                <tr>
                  <td colSpan={2 + (extraColumns?.length || 0) + (actionSummaryFn ? 1 : 0) + (timingSummaryFn ? 1 : 0) + 2} className="no-results">
                    {t('rules.noMatchingRules', 'No rules match the current filters.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          isOpen={true}
          title={t('common.delete', 'Delete')}
          message={t('rules.confirmDeleteMessage', 'Are you sure you want to delete the rule "{{name}}"?', { name: deleteConfirm.name })}
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          type="warning"
        />
      )}
    </div>
  );
};

export default RuleListView;
